import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { PaginationVisibilityInput } from '#layers/ui/public';
import { computed, onScopeDispose, ref, toValue, watch } from 'vue';
import { hasMultiplePages, hasPaginationContent } from '#layers/ui/public';

/** Which of the header count label and the page controls a related item list shows. */
export interface RelatedListChrome {
	showCountLabel: boolean;
	showPagination: boolean;
}

export interface UseRelatedListChromeOptions {
	pagination: MaybeRefOrGetter<Omit<PaginationVisibilityInput, 'loading'> | undefined>;
	loading: MaybeRefOrGetter<boolean | undefined>;
	/**
	 * Identity of the list across remounts. A list re-created under the same key while its first
	 * load runs starts from the chrome its predecessor settled on.
	 */
	stateKey?: MaybeRefOrGetter<string | undefined>;
}

export interface UseRelatedListChromeReturn {
	showCountLabel: ComputedRef<boolean>;
	showPagination: ComputedRef<boolean>;
}

/**
 * How long after a list is disposed its settled chrome is handed to a list re-created under the
 * same key. Long enough for a page remount (a create form navigating to the created item), short
 * enough that a later visit does not inherit a stale header.
 */
export const RELATED_LIST_CHROME_HYSTERESIS_MS = 1000;

interface HandedOverChrome {
	chrome: RelatedListChrome;
	disposedAt: number;
}

const handedOverChromeByKey = new Map<string, HandedOverChrome>();

/** Consumes the chrome a disposed list left under `stateKey`, if it is still within the window. */
function takeHandedOverChrome(stateKey: string | undefined): RelatedListChrome | null {
	if (stateKey === undefined) return null;

	const entry = handedOverChromeByKey.get(stateKey);
	if (!entry) return null;

	handedOverChromeByKey.delete(stateKey);

	return Date.now() - entry.disposedAt <= RELATED_LIST_CHROME_HYSTERESIS_MS ? entry.chrome : null;
}

/**
 * Whether a related item list shows its count label and its pagination bar.
 *
 * While `loading` is true both flags keep the values from the last finished load, so a page change
 * or a refetch after save doesn't toggle the header or the footer. Before the first load finishes
 * both are hidden, since we don't know yet whether the list is empty.
 *
 * Remounts need special handling: creating the parent item navigates to it, which remounts the page
 * and gives the card a fresh instance with `loading` true. Its header would go blank until the
 * first load finished. So a disposed list stores its flags under `stateKey`, and a list created under
 * the same key within `RELATED_LIST_CHROME_HYSTERESIS_MS` starts from those flags instead.
 */
export function useRelatedListChrome(options: UseRelatedListChromeOptions): UseRelatedListChromeReturn {
	const liveChrome = computed<RelatedListChrome>(() => {
		const pagination = toValue(options.pagination);

		return {
			showCountLabel: !pagination || !hasMultiplePages(pagination),
			showPagination: !!pagination && hasPaginationContent(pagination),
		};
	});

	const isLoading = () => !!toValue(options.loading);

	const handedOverChrome = takeHandedOverChrome(toValue(options.stateKey));
	const settledChrome = ref<RelatedListChrome | null>(isLoading() ? handedOverChrome : liveChrome.value);

	watch([liveChrome, isLoading], () => {
		if (!isLoading()) settledChrome.value = liveChrome.value;
	});

	onScopeDispose(() => {
		const stateKey = toValue(options.stateKey);

		if (stateKey !== undefined && settledChrome.value) {
			handedOverChromeByKey.set(stateKey, { chrome: settledChrome.value, disposedAt: Date.now() });
		}
	});

	return {
		showCountLabel: computed(() => settledChrome.value?.showCountLabel ?? false),
		showPagination: computed(() => settledChrome.value?.showPagination ?? false),
	};
}
